import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, CheckCircle2, Loader2, Sparkles, User } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { fetchBlogPost, fetchPackages, fetchServices } from "@/lib/content";
import DOMPurify from "isomorphic-dompurify";
import { optimizedImg, optimizedSrcSet } from "@/lib/img-url";
import { RelatedArticles } from "@/components/site/RelatedArticles";
import { breadcrumbScript } from "@/lib/breadcrumb-jsonld";
import { jsonLdScript } from "@/lib/jsonld";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { buildPostFaqs } from "@/lib/post-faqs";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await fetchBlogPost(params.slug);
    return { post };
  },
  head: ({ params, loaderData }) => {
    const post: any = (loaderData as any)?.post ?? null;
    const fallbackTitle = params.slug
      .split("-")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
    const rawTitle: string = post?.title || fallbackTitle;
    const SUFFIX = " | OnlineTaxVat";
    const fullTitle = `${rawTitle}${SUFFIX}`;
    const rawExcerpt: string =
      post?.excerpt ||
      post?.seo_description ||
      `Expert guide on ${rawTitle} — practical, NBR-aligned advice for tax & VAT compliance in Bangladesh.`;
    const description =
      rawExcerpt.length > 158 ? `${rawExcerpt.slice(0, 158).trimEnd()}…` : rawExcerpt;
    const url = `https://onlinetaxvat.com/blog/${params.slug}`;
    // Build keywords from post tags + baseline tax/VAT terms
    const postTags: string[] = Array.isArray(post?.tags)
      ? post.tags.map((t: any) => String(t)).filter(Boolean)
      : (typeof post?.tags === "string" ? post.tags.split(",").map((s: string) => s.trim()).filter(Boolean) : []);
    const baselineKeywords = ["income tax bangladesh", "vat bangladesh", "NBR", "tax return", "e-TIN", "online tax vat"];
    const keywords = Array.from(new Set([...postTags, ...baselineKeywords])).slice(0, 15).join(", ");
    const meta: any[] = [
      { title: fullTitle },
      { name: "description", content: description },
      { name: "keywords", content: keywords },
      { property: "og:title", content: rawTitle },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { name: "twitter:title", content: rawTitle },
      { name: "twitter:description", content: description },
    ];
    if (post?.cover_url) {
      meta.push({ property: "og:image", content: post.cover_url });
      meta.push({ name: "twitter:image", content: post.cover_url });
    }
    const scripts: any[] = [];
    if (post) {
      scripts.push(
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: rawTitle,
          description,
          image: [post.cover_url || "https://onlinetaxvat.com/pwa/icon-512x512.png?v=5"],
          datePublished: post.published_at ?? new Date().toISOString(),
          dateModified: post.updated_at ?? post.published_at ?? new Date().toISOString(),
          author: post.author
            ? { "@type": "Person", name: post.author }
            : { "@type": "Organization", "@id": "https://onlinetaxvat.com/#organization", name: "Online Tax VAT Bangladesh" },
          publisher: {
            "@type": "Organization",
            "@id": "https://onlinetaxvat.com/#organization",
            name: "Online Tax VAT Bangladesh",
            logo: {
              "@type": "ImageObject",
              url: "https://onlinetaxvat.com/pwa/icon-512x512.png?v=5",
              width: 512,
              height: 512,
            },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
        }),
      );
    }
    scripts.push(breadcrumbScript([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: rawTitle, path: `/blog/${params.slug}` },
    ]));
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: BlogPostPage,
});

function toTagArray(tags: any): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.map(String);
  if (typeof tags === "string") {
    try { const p = JSON.parse(tags); return Array.isArray(p) ? p.map(String) : []; } catch { return tags.split(",").map(s => s.trim()).filter(Boolean); }
  }
  return [];
}

function BlogPostPage() {
  const { slug } = Route.useParams();
  const loaderData = Route.useLoaderData() as { post: any } | undefined;
  const initialPost = loaderData?.post ?? null;
  const { i18n } = useTranslation();
  const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");
  const { data: post = initialPost, isLoading } = useQuery({
    queryKey: ["blog_post", slug],
    queryFn: () => fetchBlogPost(slug),
    initialData: initialPost ?? undefined,
  });
  const { data: packages = [] } = useQuery({ queryKey: ["packages"], queryFn: fetchPackages });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: fetchServices });

  if (isLoading) {
    return <SiteShell><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></SiteShell>;
  }
  if (!post) {
    return (
      <SiteShell>
        <div className="container-page py-32 text-center">
          <h1 className="font-display text-3xl font-bold">Article Not Found</h1>
          <p className="text-muted-foreground mt-3">The article you're looking for doesn't exist.</p>
          <Button asChild className="mt-6"><Link to="/blog">Back to Knowledge Hub</Link></Button>
        </div>
      </SiteShell>
    );
  }

  const tags = toTagArray((post as any).tags);
  const lowered = tags.map(t => t.toLowerCase());

  // Match: tag matches a service slug OR service title (case-insensitive contains)
  const matchedService = (services as any[]).find((s) =>
    lowered.some((t) => t === s.slug?.toLowerCase() || s.title?.toLowerCase().includes(t) || t.includes(s.slug?.toLowerCase() ?? ""))
  );

  // Find packages either by matched service or by tag keyword in name/description
  let relatedPackages = matchedService
    ? (packages as any[]).filter((p) => p.services?.slug === matchedService.slug)
    : (packages as any[]).filter((p) =>
        lowered.some((t) =>
          p.services?.title?.toLowerCase().includes(t) ||
          p.name?.toLowerCase().includes(t) ||
          (p.services?.slug ?? "").toLowerCase().includes(t)
        )
      );
  relatedPackages = relatedPackages.slice(0, 3);

  const ctaTitle = (isBn && (matchedService as any)?.title_bn) || matchedService?.title || "our premium service packages";
  const serviceSlug = matchedService?.slug;

  return (
    <SiteShell>
      <article className="pt-14 md:pt-20 pb-20">
        <div className="container-page max-w-4xl">
          <Button asChild variant="ghost" size="sm" className="mb-6 -ml-2">
            <Link to="/blog"><ArrowLeft className="mr-1.5 h-4 w-4" /> All Articles</Link>
          </Button>

          <div className="flex flex-wrap gap-2 mb-4">
            {tags.map((t) => (
              <Badge key={t} variant="secondary" className="bg-primary/10 text-primary border-primary/20">{t}</Badge>
            ))}
          </div>

          <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight">{isBn && (post as any).title_bn ? (post as any).title_bn : post.title}</h1>

          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {post.author && <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {post.author}</span>}
            {post.published_at && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            )}
          </div>

          {post.cover_url && (
            <div className="mt-8 rounded-3xl overflow-hidden shadow-card bg-muted flex items-center justify-center aspect-[16/9]">
              <img
                src={optimizedImg(post.cover_url, { width: 1200, quality: 82, resize: "contain" })}
                srcSet={optimizedSrcSet(post.cover_url, [640, 960, 1200, 1600], { quality: 82, resize: "contain" })}
                sizes="(max-width: 768px) 100vw, 900px"
                alt={`Online VAT and Tax Latest Update Bangladesh — ${(isBn && (post as any).title_bn) || post.title}`}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width={1200}
                height={675}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {(isBn && (post as any).excerpt_bn ? (post as any).excerpt_bn : post.excerpt) && (
            <p className="mt-8 text-lg leading-relaxed text-muted-foreground border-l-4 border-primary pl-5 italic">{isBn && (post as any).excerpt_bn ? (post as any).excerpt_bn : post.excerpt}</p>
          )}

          {(isBn && (post as any).content_bn ? (post as any).content_bn : post.content) && (
            <div
              className="prose prose-lg dark:prose-invert max-w-none mt-10 prose-headings:font-display prose-headings:tracking-tight prose-h2:text-2xl prose-h3:text-xl prose-a:text-primary prose-strong:text-foreground"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(isBn && (post as any).content_bn ? (post as any).content_bn : post.content) }}
            />
          )}

          {/* Package CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-14 relative overflow-hidden rounded-3xl gradient-primary p-8 md:p-10 text-primary-foreground shadow-glow"
          >
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
            <div className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" /> Get Started Today
              </div>
              <h3 className="mt-4 font-display font-bold text-2xl md:text-3xl leading-snug">
                Need help with {ctaTitle}?
              </h3>
              <p className="mt-2 text-primary-foreground/90 max-w-2xl">
                Let our expert team handle it for you — fast, secure, and 100% compliant with NBR regulations.
              </p>

              {relatedPackages.length > 0 && (
                <div className="mt-6 grid sm:grid-cols-3 gap-3">
                  {relatedPackages.map((p) => (
                    <Link
                      key={p.id}
                      to="/upload"
                      search={{ service: p.services?.slug ?? "", package: p.slug, packageId: p.id } as any}
                      className="block rounded-2xl bg-white/15 hover:bg-white/25 backdrop-blur border border-white/20 p-4 transition-colors"
                    >
                      <p className="text-xs uppercase tracking-wider opacity-80">{(isBn && p.services?.title_bn) || p.services?.title}</p>
                      <p className="font-display font-bold text-base mt-1">{p.name}</p>
                      <p className="text-sm opacity-90 mt-1">৳ {Number(p.price).toLocaleString?.() ?? p.price}</p>
                      <span className="mt-2 inline-flex items-center text-xs font-semibold">Order now <ArrowRight className="ml-1 h-3 w-3" /></span>
                    </Link>
                  ))}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/95">
                  <Link to="/packages" search={serviceSlug ? ({ service: serviceSlug } as any) : ({} as any)}>
                    View All Packages <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20">
                  <Link to="/contact">Talk to an Expert</Link>
                </Button>
              </div>
            </div>
          </motion.div>

          <SeoFaqBlock
            title={isBn ? "সাধারণ জিজ্ঞাসা" : "Frequently Asked Questions"}
            faqs={buildPostFaqs({
              title: (isBn && (post as any).title_bn) || post.title,
              tags,
              isBn,
            }).map((f) => ({ q: f.q, a: f.a }))}
          />

          <RelatedArticles currentSlug={slug} source="blog" />
        </div>
      </article>
    </SiteShell>
  );
}
